import React, { useState } from 'react';

const AMALGAMA_COLOR = '#2ec4b6';
const SECTORES_BORDE_ROJO = ['resina_indicada', 'obturacion_mala', 'amalgama_desadaptada'];

const SECTOR_LABELS = {
    caries: 'Caries',
    retraccion_gingival: 'Retracción Gingival',
    obturacion: 'Obturación en Buen Estado',
    obturacion_mala: 'Obturación En Mal Estado',
    resina_presente: 'Resina Adaptada',
    resina_indicada: 'Resina Desadaptada',
    amalgama_adaptada: 'Amalgama Adaptada',
    amalgama_desadaptada: 'Amalgama Desadaptada',
    carilla_presente: 'Carilla en Buen Estado',
    carilla_desadaptada: 'Carilla en Mal Estado',
    sellante: 'Sellante',
};

const CONDICION_LABELS = {
    ausente: 'Diente Ausente',
    extraccion: 'Diente Extraído',
    exodoncia_indicada: 'Exodoncia Indicada',
    sano: 'Diente Sano',
    pontico: 'Póntico',
    pilar_izquierdo: 'Pilar Izquierdo',
    pilar_derecho: 'Pilar Derecho',
};

const ATRIBUTO_LABELS = {
    corona: { presente: 'Corona Presente', desadaptada: 'Corona Desadaptada' },
    endodoncia: { realizada: 'Endodoncia Realizada', indicada: 'Endodoncia Indicada' },
    provisional: { presente: 'Provisional Presente', indicado: 'Provisional Indicado' },
    implante: { integrado: 'Implante Integrado', no_integrado: 'Implante No Integrado' },
    sellante: { presente: 'Sellante Presente', indicado: 'Sellante Indicado' },
    nucleo: { presente: 'Núcleo Presente', indicado: 'Núcleo Indicado' },
    brackets: { presente: 'Brackets' },
    rotacion: { presente: 'Rotaciones y Migraciones' },
};

const DienteSVG = ({ numero, estado, onSectorClick, style = {} }) => {
    const [hoverLines, setHoverLines] = useState(null);
    const patIdAzul = `resina_azul_${numero}`;

    const getTooltipLines = (sector) => {
        const lines = [];
        const estadoSector = estado?.sectores ? estado.sectores[sector] : null;
        if (estadoSector && SECTOR_LABELS[estadoSector]) {
            lines.push(SECTOR_LABELS[estadoSector]);
        }
        if (estado?.condicion && CONDICION_LABELS[estado.condicion]) {
            lines.push(CONDICION_LABELS[estado.condicion]);
        }
        Object.entries(estado?.atributos || {}).forEach(([key, value]) => {
            const label = ATRIBUTO_LABELS[key]?.[value];
            if (label) lines.push(label);
        });
        return lines;
    };

    const getColor = (sector) => {
        const estadoSector = estado?.sectores ? estado.sectores[sector] : null;
        switch (estadoSector) {
            case 'caries': return '#dc3545';
            case 'retraccion_gingival': return '#fd7e14';
            case 'obturacion': return '#007bff';
            case 'obturacion_mala': return '#007bff';
            case 'resina_presente': return `url(#${patIdAzul})`;
            case 'resina_indicada': return `url(#${patIdAzul})`;
            case 'amalgama_desadaptada': return AMALGAMA_COLOR;
            case 'amalgama_adaptada': return AMALGAMA_COLOR;
            case 'carilla_presente': return `url(#carilla_azul_${numero})`;
            case 'carilla_desadaptada': return `url(#carilla_roja_${numero})`;
            case 'sellante': return '#28a745';
            default:
                if (['pontico', 'pilar_izquierdo', 'pilar_derecho'].includes(estado?.condicion)) {
                    return 'green';
                }
                return 'white';
        }
    };

    const getBorde = (sector) => {
        const estadoSector = estado?.sectores ? estado.sectores[sector] : null;
        if (SECTORES_BORDE_ROJO.includes(estadoSector)) {
            return { stroke: '#dc3545', strokeWidth: 4.5 };
        }
        return { stroke: '#6c757d', strokeWidth: 1 };
    };

    const sectorHoverProps = (zona) => {
        const lines = getTooltipLines(zona);
        return {
            onMouseEnter: () => setHoverLines(lines.length ? lines : null),
            onMouseLeave: () => setHoverLines(null),
            onClick: () => onSectorClick(numero, zona),
        };
    };

    const paths = {
        arriba: "M0,0 L100,0 L65,35 L35,35 Z",
        abajo: "M0,100 L100,100 L65,65 L35,65 Z",
        izquierda: "M0,0 L0,100 L35,65 L35,35 Z",
        derecha: "M100,0 L100,100 L65,65 L65,35 Z",
        centro: "M35,35 L65,35 L65,65 L35,65 Z"
    };

    const renderOverlays = () => {
        const elements = [];

        if (estado?.condicion === 'extraccion') {
            elements.push(<line key="extraido" x1="50" y1="0" x2="50" y2="100" stroke="red" strokeWidth="6" style={{ pointerEvents: 'none' }} />);
        } else if (estado?.condicion === 'ausente') {
            elements.push(<line key="ausente" x1="50" y1="0" x2="50" y2="100" stroke="black" strokeWidth="6" style={{ pointerEvents: 'none' }} />);
        } else if (estado?.condicion === 'exodoncia_indicada') {
            elements.push(
                <g key="exodoncia_indicada" style={{ pointerEvents: 'none' }}>
                    <line x1="10" y1="10" x2="90" y2="90" stroke="red" strokeWidth="6" />
                    <line x1="90" y1="10" x2="10" y2="90" stroke="red" strokeWidth="6" />
                </g>
            );
        } else if (estado?.condicion === 'sano') {
            elements.push(<text key="sano" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fill="blue" fontWeight="bold" style={{ pointerEvents: 'none', opacity: 0.8 }}>✔</text>)
        } else if (estado?.condicion === 'pontico') {
            elements.push(<line key="pontico_v" x1="50" y1="0" x2="50" y2="100" stroke="red" strokeWidth="6" style={{ pointerEvents: 'none' }} />);
            elements.push(<line key="pontico_l1" x1="0" y1="45" x2="15" y2="45" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
            elements.push(<line key="pontico_l2" x1="0" y1="55" x2="15" y2="55" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
            elements.push(<line key="pontico_r1" x1="85" y1="45" x2="100" y2="45" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
            elements.push(<line key="pontico_r2" x1="85" y1="55" x2="100" y2="55" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
        } else if (estado?.condicion === 'pilar_izquierdo') {
            elements.push(<line key="pil_izq_1" x1="0" y1="45" x2="15" y2="45" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
            elements.push(<line key="pil_izq_2" x1="0" y1="55" x2="15" y2="55" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
        } else if (estado?.condicion === 'pilar_derecho') {
            elements.push(<line key="pil_der_1" x1="85" y1="45" x2="100" y2="45" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
            elements.push(<line key="pil_der_2" x1="85" y1="55" x2="100" y2="55" stroke="black" strokeWidth="4" style={{ pointerEvents: 'none' }} />);
        }

        if (estado?.atributos?.corona === 'presente') {
            elements.push(<circle key="cop" cx="50" cy="50" r="35" stroke="blue" strokeWidth="3" fill="none" style={{ pointerEvents: 'none' }} />);
        } else if (estado?.atributos?.corona === 'desadaptada') {
            elements.push(<circle key="cod" cx="50" cy="50" r="35" stroke="red" strokeWidth="3" fill="none" style={{ pointerEvents: 'none' }} />);
        }

        if (estado?.atributos?.endodoncia) {
            const isSuperior = (numero >= 11 && numero <= 28) || (numero >= 51 && numero <= 65);
            const color = estado.atributos.endodoncia === 'realizada' ? 'blue' : 'red';
            const key = estado.atributos.endodoncia === 'realizada' ? 'endr' : 'endi';
            const pathData = isSuperior
                ? "M20,15 L80,15 L50,85 Z"
                : "M50,15 L20,85 L80,85 Z";
            elements.push(<path key={key} d={pathData} fill="none" stroke={color} strokeWidth="3" style={{ pointerEvents: 'none' }} />);
        }

        if (estado?.atributos?.nucleo === 'presente') {
            elements.push(<path key="nucp" d="M25,95 L75,95 L50,55 Z" fill="green" stroke="green" strokeWidth="1" style={{ pointerEvents: 'none', opacity: 0.6 }} />);
        } else if (estado?.atributos?.nucleo === 'indicado') {
            elements.push(<path key="nuci" d="M25,95 L75,95 L50,55 Z" fill="red" stroke="red" strokeWidth="1" style={{ pointerEvents: 'none', opacity: 0.6 }} />);
        }

        if (estado?.atributos?.provisional === 'presente') {
            elements.push(<text key="prp" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fontWeight="bold" fill="blue" style={{ pointerEvents: 'none', textShadow: '0 0 2px white' }}>P</text>);
        } else if (estado?.atributos?.provisional === 'indicado') {
            elements.push(<text key="pri" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fontWeight="bold" fill="red" style={{ pointerEvents: 'none', textShadow: '0 0 2px white' }}>P</text>);
        }

        if (estado?.atributos?.implante === 'integrado') {
            elements.push(<text key="imi" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fontWeight="bold" fill="blue" style={{ pointerEvents: 'none', textShadow: '0 0 2px white' }}>I</text>);
        } else if (estado?.atributos?.implante === 'no_integrado') {
            elements.push(<text key="imn" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fontWeight="bold" fill="red" style={{ pointerEvents: 'none', textShadow: '0 0 2px white' }}>I</text>);
        }

        if (estado?.atributos?.sellante === 'presente') {
            elements.push(<text key="sep" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fontWeight="bold" fill="blue" style={{ pointerEvents: 'none', textShadow: '0 0 2px white' }}>S</text>);
        } else if (estado?.atributos?.sellante === 'indicado') {
            elements.push(<text key="sei" x="50" y="50" textAnchor="middle" dy=".3em" fontSize="60" fontWeight="bold" fill="red" style={{ pointerEvents: 'none', textShadow: '0 0 2px white' }}>S</text>);
        }

        if (estado?.atributos?.brackets === 'presente') {
            elements.push(
                <g key="brk" style={{ pointerEvents: 'none' }}>
                    <line x1="10" y1="50" x2="90" y2="50" stroke="red" strokeWidth="4" />
                    <rect x="35" y="35" width="30" height="30" fill="red" stroke="red" strokeWidth="2" />
                    <rect x="42" y="45" width="16" height="10" fill="white" />
                </g>
            );
        }

        if (estado?.atributos?.rotacion === 'presente') {
            elements.push(
                <g key="rot" style={{ pointerEvents: 'none' }}>
                    <line x1="5" y1="68" x2="95" y2="68" stroke="blue" strokeWidth="3" />
                    <polyline points="15,63 5,68 15,73" fill="none" stroke="blue" strokeWidth="3" />
                    <line x1="5" y1="76" x2="95" y2="76" stroke="blue" strokeWidth="3" />
                    <polyline points="85,71 95,76 85,81" fill="none" stroke="blue" strokeWidth="3" />
                </g>
            );
        }

        return elements;
    };

    const bordeArriba = getBorde('caja_arriba');
    const bordeAbajo = getBorde('caja_abajo');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '2px', position: 'relative', ...style }}>
            {hoverLines && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 30,
                        background: '#1f2937',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        padding: '5px 8px',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                    }}
                >
                    {hoverLines.map((line) => (
                        <div key={line}>{line}</div>
                    ))}
                </div>
            )}
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '1px' }}>{numero}</span>
            <svg width="42" height="72" viewBox="0 0 100 168" style={{ cursor: 'pointer', backgroundColor: '#fff' }}>
                <defs>
                    <pattern id={patIdAzul} patternUnits="userSpaceOnUse" width="10" height="10">
                        <rect width="10" height="10" fill="white" />
                        <path d="M0,10 L10,0 M-2,2 L2,-2 M8,12 L12,8" stroke="blue" strokeWidth="2" />
                        <path d="M0,0 L10,10 M-2,8 L2,12 M8,-2 L12,2" stroke="blue" strokeWidth="2" />
                    </pattern>
                    <pattern id={`carilla_azul_${numero}`} patternUnits="userSpaceOnUse" width="6" height="6">
                        <line x1="3" y1="0" x2="3" y2="6" stroke="blue" strokeWidth="2" />
                    </pattern>
                    <pattern id={`carilla_roja_${numero}`} patternUnits="userSpaceOnUse" width="6" height="6">
                        <line x1="3" y1="0" x2="3" y2="6" stroke="red" strokeWidth="2" />
                    </pattern>
                </defs>

                <rect
                    x="0.5" y="0.5" width="99" height="34"
                    fill={getColor('caja_arriba')}
                    stroke={bordeArriba.stroke}
                    strokeWidth={bordeArriba.strokeWidth}
                    {...sectorHoverProps('caja_arriba')}
                />

                <g transform="translate(0, 34)">
                    <rect x="0" y="0" width="100" height="100" fill="#fff" />
                    <path d={paths.arriba} fill={getColor('arriba')} {...getBorde('arriba')} {...sectorHoverProps('arriba')} />
                    <path d={paths.abajo} fill={getColor('abajo')} {...getBorde('abajo')} {...sectorHoverProps('abajo')} />
                    <path d={paths.izquierda} fill={getColor('izquierda')} {...getBorde('izquierda')} {...sectorHoverProps('izquierda')} />
                    <path d={paths.derecha} fill={getColor('derecha')} {...getBorde('derecha')} {...sectorHoverProps('derecha')} />
                    <path d={paths.centro} fill={getColor('centro')} {...getBorde('centro')} {...sectorHoverProps('centro')} />
                    {renderOverlays()}
                    <rect x="0.5" y="0.5" width="99" height="99" fill="none" stroke="#6c757d" strokeWidth="1" style={{ pointerEvents: 'none' }} />
                </g>

                <rect
                    x="0.5" y="133.5" width="99" height="34"
                    fill={getColor('caja_abajo')}
                    stroke={bordeAbajo.stroke}
                    strokeWidth={bordeAbajo.strokeWidth}
                    {...sectorHoverProps('caja_abajo')}
                />
            </svg>
        </div>
    );
};

export default DienteSVG;
